/**
 * Lightweight motion primitives for the PoP + AI-Enabled MBA portals.
 *
 * Design goals:
 *  · Zero new dependencies (uses CSS transitions + IntersectionObserver + rAF).
 *  · Respects `prefers-reduced-motion` — bypasses animation entirely.
 *  · Fires only ONCE per element (no thrashing on scroll-out / scroll-in).
 */
import React, { useEffect, useRef, useState } from 'react';

// -----------------------------------------------------------------------
// prefers-reduced-motion — cached
// -----------------------------------------------------------------------
const _prefersReducedMotion = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// -----------------------------------------------------------------------
// Reveal — fade-up + slight rise once in view.
// -----------------------------------------------------------------------
export const Reveal = ({
  as: Tag = 'div',
  delay = 0,
  duration = 700,
  y = 14,
  threshold = 0.12,
  rootMargin = '0px 0px -60px 0px',
  className = '',
  style = {},
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (_prefersReducedMotion()) { setShown(true); return; }
    const el = ref.current;
    if (!el) return;

    // Fast path — if the element is already in the initial viewport at mount,
    // fire the reveal on the next frame instead of waiting for the observer.
    // This makes above-the-fold heros animate reliably without IO edge cases.
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) {
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { setShown(true); io.unobserve(el); }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translate3d(0,0,0)' : `translate3d(0, ${y}px, 0)`,
        transition: `opacity ${duration}ms cubic-bezier(0.22,0.61,0.36,1), transform ${duration}ms cubic-bezier(0.22,0.61,0.36,1)`,
        transitionDelay: `${delay}ms`,
        willChange: 'opacity, transform',
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

// -----------------------------------------------------------------------
// CountUp — smoothly animates a number from 0 → target once in view.
// -----------------------------------------------------------------------
export const CountUp = ({
  value = 0,
  duration = 1400,
  formatter,
  className = '',
  prefix = '',
  suffix = '',
  'data-testid': testId,
}) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  const target = Number(value) || 0;

  useEffect(() => {
    if (_prefersReducedMotion()) { setDisplay(target); return; }
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || started.current) return;
          started.current = true;
          io.unobserve(el);
          const start = performance.now();
          const from = 0;
          const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const v = Math.floor(from + (target - from) * easeOutCubic(t));
            setDisplay(v);
            if (t < 1) requestAnimationFrame(tick);
            else setDisplay(target);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  const formatted = formatter ? formatter(display) : display.toLocaleString();
  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}{formatted}{suffix}
    </span>
  );
};

// -----------------------------------------------------------------------
// AnimatedGradientBg — subtle, tasteful animated aurora behind heroes.
// Uses conic + radial gradients + a slow keyframe drift. Pointer-events none.
// -----------------------------------------------------------------------
export const AnimatedGradientBg = ({ className = '' }) => (
  <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
    <div className="aurora-orb aurora-orb-1" />
    <div className="aurora-orb aurora-orb-2" />
    <div className="aurora-orb aurora-orb-3" />
    <div className="grain-overlay" />
    <style>{`
      @keyframes aurora-drift {
        0%   { transform: translate3d(-10%, -10%, 0) scale(1); }
        50%  { transform: translate3d(10%, 10%, 0) scale(1.15); }
        100% { transform: translate3d(-10%, -10%, 0) scale(1); }
      }
      .aurora-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(80px);
        opacity: 0.35;
        will-change: transform;
      }
      .aurora-orb-1 {
        width: 640px; height: 640px;
        left: -180px; top: -220px;
        background: radial-gradient(circle at center, rgba(251, 191, 36, 0.35) 0%, rgba(251, 191, 36, 0) 65%);
        animation: aurora-drift 22s ease-in-out infinite;
      }
      .aurora-orb-2 {
        width: 520px; height: 520px;
        right: -160px; top: 20%;
        background: radial-gradient(circle at center, rgba(56, 189, 248, 0.22) 0%, rgba(56, 189, 248, 0) 65%);
        animation: aurora-drift 28s ease-in-out infinite reverse;
      }
      .aurora-orb-3 {
        width: 560px; height: 560px;
        left: 30%; bottom: -220px;
        background: radial-gradient(circle at center, rgba(52, 211, 153, 0.18) 0%, rgba(52, 211, 153, 0) 65%);
        animation: aurora-drift 34s ease-in-out infinite;
      }
      .grain-overlay {
        position: absolute; inset: 0;
        background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.4'/></svg>");
        opacity: 0.045;
        mix-blend-mode: overlay;
      }
      @media (prefers-reduced-motion: reduce) {
        .aurora-orb { animation: none; }
      }
    `}</style>
  </div>
);
